<template>
  <div class="px-16 md:w-568 mx-auto">
    <h1 class="text-h2 mb-16 text-center">About your family</h1>
    <p class="text-body mb-24 text-center">Tells us a little more about your children needing care.</p>

    <Card :clickable="false" class="w-full block md:w-368 mx-auto md:p-24">
      <form>
        <Dropdown
          id="children"
          v-model="numberOfChildren"
          labelName="Number of children"
          class="mb-32"
          name="children"
          :options="childrenNumberOptions"
          :status="errors.numberOfChildren ? HelperStatus.Error : HelperStatus.Neutral"
          :helperText="errors.numberOfChildren"
          @update:model-value="clearError('numberOfChildren')"
        />
        <div class="mb-32">
          <legend id="age-of-children" class="text-strong-body mb-16">Age of children</legend>
          <Checkbox
            v-for="age in ageOptions"
            :id="age.value"
            :key="age.value"
            v-model="ageGroups"
            :labelName="age.label"
            :value="age.value"
            @update:model-value="clearError('ageGroups')"
          />
          <HelperText v-if="errors.ageGroups" :status="HelperStatus.Error" :text="errors.ageGroups" />
        </div>
        <div class="flex flex-row-reverse">
          <RouteStepperNextButton
            :stepData="reviewSection"
            :draftData="draftData"
            :validate="validate"
            @errors="errors = $event"
          />
        </div>
      </form>
    </Card>
  </div>
</template>

<script lang="ts">
import pluralize from 'pluralize';
import { PropType } from 'vue';
import { HelperStatus } from '@/helpers/status';
import Dropdown from '@/components/dropdown/Dropdown.vue';
import Checkbox from '@/components/checkbox/Checkbox.vue';
import Card from '@/components/card/Card.vue';
import HelperText from '@/components/helper_text/HelperText.vue';
import { ReviewSection } from '@/components/route_stepper/types';
import { JobPostDraft } from './types';
import RouteStepperNextButton from '../../components/route_stepper/RouteStepperNextButton.vue';
import { AgeGroups } from '@/store/modules/job/types';

interface Errors {
  numberOfChildren?: string;
  ageGroups?: string;
}

interface JobPostFamilyDetailsData {
  numberOfChildren: number;
  ageGroups: AgeGroups[];
  errors: Errors;
}

export default {
  name: 'JobPostFamilyDetails',
  components: {
    Dropdown,
    Checkbox,
    Card,
    HelperText,
    RouteStepperNextButton,
  },
  props: {
    draft: {
      type: Object as PropType<JobPostDraft>,
      required: true,
    },
  },
  data(): JobPostFamilyDetailsData {
    return {
      numberOfChildren: this.draft.numberOfChildren || 0,
      ageGroups: this.draft.ageGroups || [],
      errors: {},
    };
  },
  computed: {
    HelperStatus(): typeof HelperStatus {
      return HelperStatus;
    },
    childrenNumberOptions(): { value: number; display: string }[] {
      return [
        { value: 1, display: '1' },
        { value: 2, display: '2' },
        { value: 3, display: '3' },
        { value: 4, display: '4' },
        { value: 5, display: '5+' },
      ];
    },
    ageOptions(): { value: AgeGroups; label: string }[] {
      return [
        { value: AgeGroups.INFANT, label: 'Infant (0–11 months)' },
        { value: AgeGroups.TODDLER, label: 'Toddler (1–2 years)' },
        { value: AgeGroups.PRE_SCHOOL, label: 'Preschool (3–5 years)' },
        { value: AgeGroups.ELEMENTARY, label: 'Elementary (6–10 years)' },
        { value: AgeGroups.JUNIOR_HIGH, label: 'Middle school (11+ years)' },
      ];
    },
    numberOfChildrenSentence(): string {
      return pluralize('child', this.numberOfChildren, true);
    },
    ageGroupsSentence(): string {
      const mappedAgeGroupsArray = [
        { [AgeGroups.INFANT]: 'Infant' },
        { [AgeGroups.TODDLER]: 'Toddler' },
        { [AgeGroups.PRE_SCHOOL]: 'Preschool' },
        { [AgeGroups.ELEMENTARY]: 'Elementary' },
        { [AgeGroups.JUNIOR_HIGH]: 'Middle school' },
      ];
      return mappedAgeGroupsArray
        .filter((ageGroup) => this.ageGroups.includes(Object.keys(ageGroup)[0] as AgeGroups))
        .map((ageGroup) => Object.values(ageGroup)[0])
        .join(', ');
    },
    reviewSection(): ReviewSection {
      return {
        title: 'Family',
        content: `${this.numberOfChildrenSentence} • ${this.ageGroupsSentence}`,
        editPath: this.$route.path,
      };
    },
    draftData(): JobPostDraft {
      return {
        numberOfChildren: this.numberOfChildren,
        ageGroups: this.ageGroups,
      };
    },
  },
  methods: {
    clearError(key: keyof Errors) {
      const { [key]: _removed, ...rest } = this.errors;
      this.errors = rest;
    },
    validate(): Errors {
      const errors: Errors = {};

      if (!this.numberOfChildren) {
        errors.numberOfChildren = 'This field is required.';
      }
      if (this.ageGroups.length === 0) {
        errors.ageGroups = 'Selecting at least one age is required.';
      }
      if (this.ageGroups.length > this.numberOfChildren) {
        errors.ageGroups = "Ages selected can't exceed the number of children.";
      }

      return errors;
    },
  },
};
</script>
